/**
 * ActiveSessionsBlocks — shared sub-components for ActiveSessionsPage.
 * Extracted from ActiveSessionsPage.jsx to reduce file complexity.
 */
import React from 'react';
import { LogOut, AlertTriangle, User, Clock } from 'lucide-react';

const fmt = (s) => s ? new Date(s).toLocaleString() : '—';

// ── Style constants ───────────────────────────────────────────────────────────
export const thStyle = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12,
  fontWeight: 700, color: 'var(--text-muted, #94a3b8)',
  textTransform: 'uppercase', letterSpacing: '0.05em'
};
export const tdStyle = { padding: '8px 12px', fontSize: 12, color: 'var(--text-primary, #cbd5e1)' };

// ── StatusBadge ───────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    ONLINE:  { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  label: 'Online'  },
    IDLE:    { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  label: 'Idle'    },
    OFFLINE: { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', label: 'Offline' }
  };
  const c = map[status] || map.OFFLINE;
  return (
    <span
      data-testid={`status-${status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700,
        color: c.color, background: c.bg, textTransform: 'uppercase', letterSpacing: '0.05em'
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color, boxShadow: status === 'ONLINE' ? `0 0 6px ${c.color}` : 'none' }} />
      {c.label}
    </span>
  );
}

// ── ActionBadge ───────────────────────────────────────────────────────────────
export function ActionBadge({ action }) {
  const map = {
    LOGIN:        { color: '#10b981', label: 'Login'       },
    LOGOUT:       { color: '#3b82f6', label: 'Logout'      },
    FORCE_LOGOUT: { color: '#dc2626', label: 'Force Logout' }
  };
  const c = map[action] || { color: '#94a3b8', label: action };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700,
      color: c.color, background: `${c.color}22`, textTransform: 'uppercase', letterSpacing: '0.05em'
    }}>{c.label}</span>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color, testid }) {
  return (
    <div className="card" data-testid={testid} style={{ padding: 14, borderLeft: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, marginTop: 4, color: 'var(--text-primary)' }}>{value ?? 0}</div>
    </div>
  );
}

// ── DistributionCard ──────────────────────────────────────────────────────────
export function DistributionCard({ title, data, icon, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="card" data-testid={testid}>
      <div className="card-header">
        <span className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {icon} {title}
        </span>
      </div>
      <div className="card-body" style={{ padding: 8, maxHeight: 160, overflowY: 'auto' }}>
        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', padding: 8 }}>No data</div>
        ) : entries.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', fontSize: 12, borderRadius: 4 }}>
            <span style={{ color: 'var(--text-primary)' }}>{k}</span>
            <span style={{ fontWeight: 800, color: 'var(--brand-blue, #3b82f6)' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── DetailField ───────────────────────────────────────────────────────────────
export function DetailField({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit', color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

// ── ClientDetailPanel ─────────────────────────────────────────────────────────
export function ClientDetailPanel({ session, detail, loading, onClose, onForceLogout }) {
  const c        = detail?.client   || {};
  const licenses = detail?.licenses || [];
  const child    = detail?.children || [];

  return (
    <div className="card" data-testid="client-detail-panel" style={{ borderLeft: '3px solid var(--brand-blue, #3b82f6)' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title">
          <User size={14} style={{ display: 'inline', marginRight: 6 }} />
          {session.Username} {session.CompanyName ? `\u00b7 ${session.CompanyName}` : ''}
          {session.ClientId
            ? <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>({session.ClientId})</span>
            : null}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {session.Status !== 'OFFLINE' && (
            <button
              onClick={() => onForceLogout && onForceLogout(session)}
              className="erp-btn"
              data-testid="client-detail-force-logout"
              style={{ fontSize: 12, padding: '4px 10px', color: '#dc2626', borderColor: '#fecaca' }}
              title="Force logout this session"
            >
              <LogOut size={11} /> Force Logout
            </button>
          )}
          <button onClick={onClose} className="erp-btn" data-testid="client-detail-close" style={{ fontSize: 12 }}>\u2715 Close</button>
        </div>
      </div>

      <div className="card-body">
        {/* Session-level fields — always shown */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
          <DetailField label="Status"       value={<StatusBadge status={session.Status} />} />
          <DetailField label="Role"         value={session.IsSuperAdmin ? 'Super Admin' : (session.Role || '—')} />
          <DetailField label="IP Address"   value={session.IpAddress  || '—'} mono />
          <DetailField label="Email"        value={session.Email      || '—'} />
          <DetailField label="Login At"     value={fmt(session.LoginAt)} />
          <DetailField label="Last Seen"    value={`${fmt(session.LastSeenAt)}  (${session.ageMinutes ?? '?'} min ago)`} />
          <DetailField label="License Tier" value={session.LicenseTier || '—'} />
          <DetailField label="Industry"     value={session.Industry   || '—'} />
        </div>

        {/* Client master + licenses (only when ClientId is set) */}
        {!session.ClientId ? (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', background: 'var(--surface-2)', borderRadius: 8 }}>
            <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6, color: '#f59e0b' }} />
            This is a Super-Admin / Operator session — not linked to any client tenant.
          </div>
        ) : loading ? (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
            <div className="spinning" style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid var(--brand-blue)', borderTopColor: 'transparent', borderRadius: '50%' }} />
            <p>Loading client details…</p>
          </div>
        ) : !detail || !detail.found ? (
          <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            Client record for <strong>{session.ClientId}</strong> not found in ClientMaster.
          </div>
        ) : (
          <>
            <SectionLabel>Client Master</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
              <DetailField label="Company"   value={c.CompanyName || '—'} />
              <DetailField label="Industry"  value={c.Industry    || '—'} />
              <DetailField label="Tier"      value={c.tier_code || c.Packages || '—'} />
              <DetailField label="Status"    value={c.Status      || '—'} />
              <DetailField label="Phone"     value={c.PhoneNo1 || c.PhoneNo2 || '—'} mono />
              <DetailField label="Email"     value={c.EmailId     || '—'} />
              <DetailField label="Address"   value={c.Address     || '—'} />
              <DetailField label="Country"   value={c.Country || c.State || '—'} />
              <DetailField label="Created"   value={fmt(c.CreatedAt)} />
              <DetailField label="GST / Tax" value={c.GSTNo || c.TaxId || '—'} mono />
            </div>

            <SectionLabel>Licenses ({licenses.length})</SectionLabel>
            {licenses.length === 0 ? (
              <div style={{ padding: 8, fontSize: 12, color: 'var(--text-muted)' }}>No licenses on file.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                {licenses.slice(0, 6).map((l) => (
                  <div key={l.LicenseId} className="card" style={{ padding: 10, borderTop: `2px solid ${l.Status === 'Active' ? '#10b981' : '#94a3b8'}` }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{l.LicenseTier}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2 }}>{l.Status}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'monospace' }}>{String(l.LicenseId).substring(0, 8)}…</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      <Clock size={9} style={{ display: 'inline', marginRight: 4 }} />
                      Expires {fmt(l.ExpiryDate)?.slice(0, 10)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {child.length > 0 && (
              <>
                <SectionLabel>Tenant Users ({child.length})</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {child.slice(0, 9).map((u, i) => (
                    <div key={i} style={{ padding: 6, fontSize: 12, background: 'var(--surface-2)', borderRadius: 6 }}>
                      <div style={{ fontWeight: 700 }}>{u.UserName || u.FullName || '—'}</div>
                      <div style={{ color: 'var(--text-muted)' }}>{u.UserRole || u.Role || '—'}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── SectionLabel (internal helper) ────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
      {children}
    </div>
  );
}
